import { Sites } from '@ps/platform-message-framework/common/Sites';
import { ResolverTag } from '@ps/web-core-enums';
import { Link, Translation } from '@ps/web-shared-components';
import React from 'react';
import { FormattedDate } from 'react-intl';
import { useSelector } from 'react-redux';

import { useSelfExclusionData } from '@swf/account';
import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import { select } from '@swf/core/store';

import {
  TXTACL_You_can_still_make_withdrawals_ELL,
  TXTWCL_As_a_resultX_you_will_not_be_a_ELL,
  TXTWCL_As_a_resultX_you_will_not_be_a_ELL_1,
  TXTWCL_For_more_informationX_please_v_ELL_3,
  TXTWCL_You_are_currently_listed_on_th_ELL,
  TXTWCL_You_are_currently_on_the_Gener_ELL,
  TXTWCL_You_can_still_make_withdrawals_ELL,
  TXTWCL_Your_account_has_been_suspende_ELL_4
} from './NationalExclusion.messages.json';

export const shouldShowNationalExclusion = (site: Sites): boolean =>
  [
    Sites.COM,
    Sites.FRA,
    Sites.UK,
    Sites.ROM,
    Sites.SPA,
    Sites.CA_ON,
    Sites.US_PA,
    Sites.US_NJ,
    Sites.USMI
  ].includes(site);

const siteConfigs: Partial<
  Record<
    Sites,
    {
      label: string;
      href: string;
    }
  >
> = {
  [Sites.FRA]: {
    label: 'ARJEL',
    href: 'https://anj.fr'
  },
  [Sites.UK]: {
    label: 'GAMSTOP',
    href: 'https://www.gamstop.co.uk'
  },
  [Sites.ROM]: {
    label: 'ONJN',
    href: 'https://onjn.gov.ro/'
  },
  [Sites.CA_ON]: {
    label: 'RGIAJ',
    href: 'https://www.justice.gc.ca/'
  },
  [Sites.US_PA]: {
    label: 'PGCB',
    href: 'https://gamingcontrolboard.pa.gov/'
  },
  [Sites.US_NJ]: {
    label: 'DGE',
    href: 'https://www.njoag.gov/about/divisions-and-offices/division-of-gaming-enforcement-home/'
  },
  [Sites.USMI]: {
    label: 'MGCB',
    href: 'https://www.michigan.gov/mgcb'
  }
};

const NationalExclusion: React.FC = () => {
  const site = useSelector(select.cms.getSiteId);
  const { selfExclusionData } = useSelfExclusionData();
  const { timeUntil } = selfExclusionData || {};

  const selfExclusionEndDate = timeUntil ? new Date(timeUntil) : new Date();
  const formattedEndDate = (
    <b>
      <FormattedDate
        value={selfExclusionEndDate}
        year="numeric"
        month="2-digit"
        day="2-digit"
        hour="2-digit"
        minute="2-digit"
      />
    </b>
  );
  const config = siteConfigs[site];

  if (site === Sites.COM) {
    return (
      <>
        <Translation {...TXTWCL_You_are_currently_on_the_Gener_ELL} />{' '}
        <Translation
          {...TXTWCL_As_a_resultX_you_will_not_be_a_ELL}
          values={{
            0: formattedEndDate
          }}
        />
      </>
    );
  }

  if (site === Sites.SPA) {
    return (
      <>
        <Translation tagName="span" {...TXTWCL_Your_account_has_been_suspende_ELL_4} />
        <Translation tagName="p" {...TXTWCL_You_can_still_make_withdrawals_ELL} />
        <Translation
          tagName="p"
          {...TXTWCL_For_more_informationX_please_v_ELL_3}
          values={{
            a: (msg: React.ReactNode) => (
              <ResolverLink resolverTag={ResolverTag.RgiajDepositWithdraw}>{msg}</ResolverLink>
            )
          }}
        />
      </>
    );
  }

  if (!config) return null;

  if (site === Sites.ROM) {
    return (
      <>
        <Translation
          values={{
            a: (msg: React.ReactNode) => (
              <Link target="_blank" href={config.href}>
                {msg}
              </Link>
            ),
            1: config.label
          }}
          {...TXTWCL_You_are_currently_listed_on_th_ELL}
        />{' '}
        <Translation {...TXTWCL_As_a_resultX_you_will_not_be_a_ELL_1} />{' '}
        <Translation {...TXTACL_You_can_still_make_withdrawals_ELL} />
      </>
    );
  }

  return (
    <>
      <Translation
        values={{
          a: (msg: React.ReactNode) => (
            <Link target="_blank" href={config.href}>
              {msg}
            </Link>
          ),
          1: config.label
        }}
        {...TXTWCL_You_are_currently_listed_on_th_ELL}
      />{' '}
      <Translation
        values={{ 0: formattedEndDate }}
        {...TXTWCL_As_a_resultX_you_will_not_be_a_ELL}
      />
    </>
  );
};

export default NationalExclusion;
